import { useEffect, useState } from "react";
import { GripVertical } from "lucide-react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { COLUMN_MAP, DEFAULT_COLUMNS, type ColumnState } from "./columns";

function ColumnRow({
	col,
	onToggle,
}: {
	col: ColumnState;
	onToggle: (key: ColumnState["key"]) => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: col.key });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : undefined,
		background: isDragging ? "var(--muted-subtle)" : undefined,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
		>
			<button
				type="button"
				className="text-muted hover:text-foreground cursor-grab active:cursor-grabbing"
				aria-label="Drag to reorder"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="h-4 w-4" />
			</button>
			<Checkbox
				checked={col.visible}
				onCheckedChange={() => onToggle(col.key)}
				id={`col-${col.key}`}
			/>
			<label
				htmlFor={`col-${col.key}`}
				className="flex-1 cursor-pointer text-sm text-foreground"
			>
				{COLUMN_MAP[col.key].label}
			</label>
		</div>
	);
}

export default function ColumnSettingsModal({
	open,
	onOpenChange,
	value,
	onChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	value: ColumnState[];
	onChange: (next: ColumnState[]) => void;
}) {
	const [draft, setDraft] = useState<ColumnState[]>(value);

	// Resync the draft each time the modal opens.
	useEffect(() => {
		if (open) setDraft(value);
	}, [open, value]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const apply = (next: ColumnState[]) => {
		setDraft(next);
		onChange(next);
	};

	const toggle = (key: ColumnState["key"]) =>
		apply(
			draft.map((c) =>
				c.key === key ? { ...c, visible: !c.visible } : c,
			),
		);

	const handleDragEnd = (e: DragEndEvent) => {
		const { active, over } = e;
		if (!over || active.id === over.id) return;
		const from = draft.findIndex((c) => c.key === active.id);
		const to = draft.findIndex((c) => c.key === over.id);
		if (from === -1 || to === -1) return;
		apply(arrayMove(draft, from, to));
	};

	const visibleCount = draft.filter((c) => c.visible).length;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Change columns</DialogTitle>
					<DialogDescription>
						Toggle and drag to reorder columns. Applies to the table and
						CSV export.
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[55vh] space-y-2 overflow-y-auto py-1">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={draft.map((c) => c.key)}
							strategy={verticalListSortingStrategy}
						>
							{draft.map((col) => (
								<ColumnRow
									key={col.key}
									col={col}
									onToggle={toggle}
								/>
							))}
						</SortableContext>
					</DndContext>
				</div>

				<DialogFooter className="flex items-center justify-between sm:justify-between">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => apply(DEFAULT_COLUMNS)}
					>
						Reset to default
					</Button>
					<Button
						size="sm"
						onClick={() => onOpenChange(false)}
						disabled={visibleCount === 0}
					>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
